import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  Popover,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  Assignment as NDAIcon,
  School as CertificateIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Science as ScienceIcon,
  Search as SearchIcon,
  GroupAdd as UserApprovalIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Gavel as GavelIcon,
  Notifications as NotificationsIcon,
  DoneAll as DoneAllIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { buildFileUrl, notificationService } from "../services/api";

const drawerWidth = 240;

// Función para mapear roles a español
const getRoleLabel = (role) => {
  const roleMap = {
    admin: "Administrador",
    researcher: "Investigador",
    collaborator: "Colaborador",
    student: "Estudiante",
    legal: "Estudio Jurídico",
  };
  return roleMap[role] || role;
};

function MainLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await notificationService.getUnreadCount();
      setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      // Silently ignore - notifications are non-critical
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleNotifOpen = async (event) => {
    setNotifAnchorEl(event.currentTarget);
    try {
      const { data } = await notificationService.getAll();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      // ignore
    }
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      // ignore
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      // ignore
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate("/login");
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleAdminMenu = () => {
    setAdminMenuOpen(!adminMenuOpen);
  };

  const menuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
    { text: "Buscador Público", icon: <SearchIcon />, path: "/search" },
  ];

  // Opciones según el rol
  if (isAdmin()) {
    menuItems.push(
      { text: "NDAs", icon: <NDAIcon />, path: "/ndas" },
      { text: "Certificados", icon: <CertificateIcon />, path: "/certificates" }
    );
  } else if (user?.role === "researcher") {
    menuItems.push(
      {
        text: "Mis Proyectos",
        icon: <ScienceIcon />,
        path: "/research-projects",
      },
      { text: "NDAs", icon: <NDAIcon />, path: "/ndas" }
    );
  } else if (user?.role === "collaborator") {
    menuItems.push(
      {
        text: "Mis Proyectos",
        icon: <ScienceIcon />,
        path: "/research-projects",
      },
      { text: "Mis Documentos", icon: <DocumentIcon />, path: "/documents" }
    );
  } else if (user?.role === "student") {
    menuItems.push({
      text: "Mis Proyectos",
      icon: <ScienceIcon />,
      path: "/research-projects",
    });
  } else if (user?.role === "legal") {
    menuItems.push({
      text: "Gestión de NDAs",
      icon: <GavelIcon />,
      path: "/legal/ndas",
    });
  }

  const adminMenuItems = [
    { text: "Panel Principal", icon: <AdminIcon />, path: "/admin" },
    {
      text: "Usuarios Pendientes",
      icon: <UserApprovalIcon />,
      path: "/admin/users",
    },
    {
      text: "Gestión de Usuarios",
      icon: <PeopleIcon />,
      path: "/admin/users/all",
    },
    {
      text: "Tipos de NDA",
      icon: <NDAIcon />,
      path: "/admin/nda-types",
    },
  ];

  const drawer = (
    <div>
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          gap: 1,
        }}
      >
        <Box
          component="img"
          src="/logo-unir-v.png"
          alt="UNIR - Vicerrectorado de Transferencia"
          sx={{
            width: "80%",
            maxWidth: 180,
            height: "auto",
            mb: 1,
          }}
        />
        <Typography variant="caption" align="center" color="textSecondary">
          Sistema de Firmas Digitales
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        {isAdmin() && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={toggleAdminMenu}>
                <ListItemIcon>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText primary="Administración" />
                {adminMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            {adminMenuOpen && (
              <List component="div" disablePadding>
                {adminMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => handleNavigate(item.path)}
                      selected={location.pathname === item.path}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Usuario: {user?.name}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Rol: {getRoleLabel(user?.role)}
        </Typography>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}
          >
            <Box
              component="img"
              src="/logo-unir-h.png"
              alt="UNIR"
              sx={{
                height: 40,
                display: { xs: "none", md: "block" },
              }}
            />
            <Typography variant="h6" noWrap component="div">
              {menuItems.find((item) => item.path === location.pathname)
                ?.text ||
                adminMenuItems.find((item) => item.path === location.pathname)
                  ?.text ||
                "Dashboard"}
            </Typography>
          </Box>
          <IconButton
            color="inherit"
            onClick={handleNotifOpen}
            sx={{ mr: 1 }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Popover
            open={Boolean(notifAnchorEl)}
            anchorEl={notifAnchorEl}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { width: 360, maxHeight: 400 } }}
          >
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Notificaciones
              </Typography>
              {unreadCount > 0 && (
                <IconButton size="small" onClick={handleMarkAllAsRead} title="Marcar todas como leidas">
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Divider />
            <Box sx={{ maxHeight: 320, overflow: "auto" }}>
              {notifications.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: "center" }}>
                  No hay notificaciones
                </Typography>
              ) : (
                notifications.map((notif) => (
                  <Box
                    key={notif.id}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      backgroundColor: notif.isRead ? "transparent" : "action.hover",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:hover": { backgroundColor: "action.selected" },
                    }}
                    onClick={() => {
                      if (!notif.isRead) handleMarkAsRead(notif.id);
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      {!notif.isRead && (
                        <CircleIcon sx={{ fontSize: 8, color: "primary.main", mt: 0.8 }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: "0.8rem" }}>
                          {notif.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>
                          {notif.message}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(notif.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Popover>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <Avatar
              src={user?.photo ? buildFileUrl(user.photo, "profiles") : null}
              alt={user?.name || "Usuario"}
              sx={{ width: 32, height: 32 }}
            >
              {!user?.photo &&
                (user?.name?.charAt(0).toUpperCase() || <AccountCircle />)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/profile");
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Perfil
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Cerrar Sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}

export default MainLayout;
